import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PageLoader from "./components/PageLoader";
import { FeatureGate } from "./components/FeatureGate";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Home from "./pages/Home";
import Resources from "./pages/Resources";
import ResourceDetail from "./pages/ResourceDetail";
import NotFound from "./pages/NotFound";
// Lazy load these routes to reduce initial bundle
const MyAppointments = lazy(() => import("./pages/MyAppointments"));
const Booking = lazy(() => import("./pages/Booking"));
const Profile = lazy(() => import("./pages/Profile"));
const Tracker = lazy(() => import("./pages/Tracker"));
import ResetPassword from "./pages/ResetPassword";
import PanicScreen from "./pages/PanicScreen";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import MentionsLegales from "./pages/legal/MentionsLegales";
import Cookies from "./pages/legal/Cookies";
import DPO from "./pages/legal/DPO";
import Mission from "./pages/Mission";
import Tarifs from "./pages/Tarifs";
import Support from "./pages/Support";
import Contact from "./pages/Contact";
import SecureUpgrade from "./pages/SecureUpgrade";
import AfterSession from "./pages/telehealth/AfterSession";
import TestLiveKit from "./pages/telehealth/TestLiveKit";
import { CookieBanner } from "./components/CookieBanner";

// Lazy load heavy components
const Search = lazy(() => import("./pages/Search"));
const PractitionerProfile = lazy(() => import("./pages/PractitionerProfile"));
const ProDashboard = lazy(() => import("./pages/pro/Dashboard"));
const ProOnboarding = lazy(() => import("./pages/pro/Onboarding"));
const ModerationDashboard = lazy(() => import("./pages/moderation/Dashboard"));
const AdminPractitioners = lazy(() => import("./pages/admin/Practitioners"));
const AdminPractitionerDetail = lazy(() => import("./pages/admin/PractitionerDetail"));
const AdminStats = lazy(() => import("./pages/admin/Stats"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminResources = lazy(() => import("./pages/admin/ResourcesAdmin"));
const AdminDirectory = lazy(() => import("./pages/admin/DirectoryAdmin"));
const AdminWithdrawal = lazy(() => import("./pages/admin/WithdrawalAdmin"));
const AdminCoping = lazy(() => import("./pages/admin/CopingAdmin"));
const AdminQuotes = lazy(() => import("./pages/admin/QuotesAdmin"));
const AdminCommunity = lazy(() => import("./pages/admin/CommunityAdmin"));
const AdminSettings = lazy(() => import("./pages/admin/SettingsAdmin"));
const AdminSystemHealth = lazy(() => import("./pages/admin/SystemHealth"));
const WaitingRoom = lazy(() => import("./pages/telehealth/WaitingRoom"));
const VideoRoom = lazy(() => import("./pages/telehealth/VideoRoom"));
const Withdrawal = lazy(() => import("./pages/Withdrawal"));
const Directory = lazy(() => import("./pages/Directory"));
const ResponsivePreviewToolbar = import.meta.env.DEV
  ? lazy(() => import("./components/dev/ResponsivePreviewToolbar"))
  : null;

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          {ResponsivePreviewToolbar && (
            <Suspense fallback={null}>
              <ResponsivePreviewToolbar />
            </Suspense>
          )}
          <CookieBanner />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/secure-upgrade" element={<SecureUpgrade />} />
            <Route path="/panic" element={<PanicScreen />} />
            
            {/* Legal pages */}
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/mentions" element={<MentionsLegales />} />
            <Route path="/legal/cookies" element={<Cookies />} />
            <Route path="/legal/dpo" element={<DPO />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/support" element={<Support />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Accessible routes */}
            <Route path="/home" element={<Home />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<ResourceDetail />} />
            <Route path="/profile" element={
              <Suspense fallback={<PageLoader />}>
                <Profile />
              </Suspense>
            } />
            <Route path="/tracker" element={
              <Suspense fallback={<PageLoader />}>
                <Tracker />
              </Suspense>
            } />
            <Route path="/withdrawal" element={
              <Suspense fallback={<PageLoader />}>
                <Withdrawal />
              </Suspense>
            } />
            <Route path="/directory" element={
              <Suspense fallback={<PageLoader />}>
                <Directory />
              </Suspense>
            } />

            {/* Feature-gated routes */}
            <Route path="/search" element={
              <FeatureGate title="Recherche de praticiens" description="Trouvez un·e professionnel·le spécialisé·e près de chez vous" icon="MapPin" />
            } />
            <Route path="/practitioner/:id" element={
              <FeatureGate title="Profil praticien" description="Consultez le profil détaillé d'un·e praticien·ne" icon="Stethoscope" />
            } />
            <Route path="/booking/:practitionerId" element={
              <FeatureGate title="Prise de rendez-vous" description="Réservez une consultation avec un·e spécialiste" icon="Calendar" />
            } />
            <Route path="/my-appointments" element={
              <FeatureGate title="Mes rendez-vous" description="Gérez vos consultations passées et à venir" icon="Calendar" />
            } />
            <Route path="/tarifs" element={
              <FeatureGate title="Tarifs et abonnements" description="Découvrez nos offres d'accompagnement" icon="CreditCard" />
            } />

            {/* Feature-gated practitioner routes */}
            <Route path="/pro/onboarding" element={
              <FeatureGate title="Espace praticien" description="Rejoignez notre réseau de professionnels" icon="Stethoscope" />
            } />
            <Route path="/pro/dashboard" element={
              <FeatureGate title="Dashboard praticien" description="Gérez votre agenda et vos patients" icon="Stethoscope" />
            } />

            {/* Feature-gated telehealth routes */}
            <Route path="/telehealth/waiting/:appointmentId" element={
              <FeatureGate title="Téléconsultation" description="Consultez un·e spécialiste en vidéo" icon="Video" />
            } />
            <Route path="/telehealth/room/:appointmentId" element={
              <FeatureGate title="Téléconsultation" description="Consultez un·e spécialiste en vidéo" icon="Video" />
            } />
            <Route path="/telehealth/after/:appointmentId" element={
              <FeatureGate title="Téléconsultation" description="Consultez un·e spécialiste en vidéo" icon="Video" />
            } />
            <Route path="/telehealth/test" element={
              <FeatureGate title="Téléconsultation" description="Consultez un·e spécialiste en vidéo" icon="Video" />
            } />
            
            {/* Moderation routes */}
            <Route path="/moderation" element={
              <ProtectedRoute requiredRole="moderator">
                <Suspense fallback={<PageLoader />}>
                  <ModerationDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* Admin routes */}
            {[
              { path: "/admin", element: <AdminDashboard /> },
              { path: "/admin/resources", element: <AdminResources /> },
              { path: "/admin/directory", element: <AdminDirectory /> },
              { path: "/admin/withdrawal", element: <AdminWithdrawal /> },
              { path: "/admin/coping", element: <AdminCoping /> },
              { path: "/admin/quotes", element: <AdminQuotes /> },
              { path: "/admin/community", element: <AdminCommunity /> },
              { path: "/admin/settings", element: <AdminSettings /> },
              { path: "/admin/system-health", element: <AdminSystemHealth /> },
            ].map(({ path, element }) => (
              <Route key={path} path={path} element={
                <ProtectedRoute requiredRole="admin">
                  <Suspense fallback={<PageLoader />}>{element}</Suspense>
                </ProtectedRoute>
              } />
            ))}
            <Route path="/admin/practitioners" element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <AdminPractitioners />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/practitioners/:id" element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <AdminPractitionerDetail />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/stats" element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <AdminStats />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
